/**
 * What this chain can emit, and what it has emitted.
 *
 * The event half of the surface, and the half a chain analyst reads for
 * narrative. A dispatchable says what somebody *can* ask for; an event says
 * what the runtime *does*, including the parts nobody dispatches directly — a
 * vesting schedule ending, an account reaped, a proof verified.
 *
 * Silence is a finding. `Vesting::VestingCompleted` at zero and
 * `System::CodeUpdated` at zero are two different statements about a chain's
 * life so far, and neither is visible on an explorer that lists only what
 * happened. Which is why nothing is filtered out of this table for being
 * unused — only three per-block, account-less kinds are skipped on the way into
 * the index, and those say so rather than showing a zero that reads as disuse.
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import type { CallIndex } from '../api/generated/CallIndex'
import { RequestFailed, fetchCalls } from '../api/rest'
import { ago, height as fmtHeight } from '../lib/format'
import { href } from '../lib/routes'

export function EventsIndex({ chain }: { chain: string }) {
  const [index, setIndex] = useState<CallIndex | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showUnused, setShowUnused] = useState(true)

  useEffect(() => {
    const controller = new AbortController()
    setIndex(null)
    setError(null)
    fetchCalls(chain, controller.signal)
      .then(setIndex)
      .catch((e: unknown) => {
        if (controller.signal.aborted) return
        setError(e instanceof RequestFailed ? e.message : 'Could not reach the observer.')
      })
    return () => controller.abort()
  }, [chain])

  const rows = index ? index.events.filter((e) => showUnused || e.emitted > 0) : []

  return (
    <section className="panel" style={{ marginBottom: 26 }}>
      <div className="panel-head">
        <h2 className="panel-title">Events</h2>
        {index && (
          <span className="eyebrow">
            {index.fired} of {index.events.length} fired
            {index.spec_version !== null && ` · runtime v${index.spec_version}`}
          </span>
        )}
      </div>

      {error && <p className="empty">{error}</p>}
      {!error && index === null && <p className="empty">Reading the runtime…</p>}

      {index !== null && (
        <>
          <div className="pager" style={{ borderTop: 0, borderBottom: 'var(--rule)' }}>
            <button
              className="segmented panel-button"
              aria-pressed={showUnused}
              onClick={() => setShowUnused(!showUnused)}
            >
              {showUnused ? '✓ Showing never fired' : 'Show never fired'}
            </button>
          </div>

          <div className="scroll-x">
            <table className="board">
              <caption className="visually-hidden">
                Every event the runtime declares, with how often it has fired
              </caption>
              <thead>
                <tr>
                  <th scope="col">#</th>
                  <th scope="col" className="left">
                    Event
                  </th>
                  <th scope="col">Fired</th>
                  <th scope="col">Accounts</th>
                  <th scope="col" className="event-when">
                    Last seen
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((e) => (
                  <tr
                    key={`${e.pallet}.${e.variant}`}
                    className={e.emitted === 0 ? 'call-unused' : undefined}
                  >
                    <td className="numeral">
                      <span className="runtime-index">
                        {e.pallet_index}.{e.variant_index}
                      </span>
                    </td>
                    <td className="left">
                      <div className="call-cell">
                        {e.emitted > 0 ? (
                          <Link to={href({ chain, event: [e.pallet, e.variant] })}>
                            <code>
                              {e.pallet}::{e.variant}
                            </code>
                          </Link>
                        ) : (
                          <code>
                            {e.pallet}::{e.variant}
                          </code>
                        )}
                        {e.signature && <span className="runtime-type">({e.signature})</span>}
                        {e.docs && <span className="runtime-doc">{e.docs}</span>}
                      </div>
                    </td>
                    <td className="numeral">
                      {/* A zero because we do not keep them is not a zero
                          because it never happened, and the difference is the
                          whole point of this column. */}
                      {e.skipped ? (
                        <span
                          className="runtime-modifier"
                          title="Fires every block and names no account, so it is deliberately not indexed — see SKIPPED_EVENTS."
                        >
                          not indexed
                        </span>
                      ) : e.emitted === 0 ? (
                        <span className="runtime-modifier">never</span>
                      ) : (
                        fmtHeight(e.emitted)
                      )}
                    </td>
                    <td className="numeral">
                      {e.skipped || e.emitted === 0 ? '—' : fmtHeight(e.accounts)}
                    </td>
                    <td className="numeral event-when" style={{ color: 'var(--text-muted)' }}>
                      {e.last_at
                        ? ago(e.last_at)
                        : e.last_height
                          ? `#${fmtHeight(e.last_height)}`
                          : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="panel-note">
            Declared by the runtime, counted from this observer's event index over blocks{' '}
            {index.indexed_from !== null && index.indexed_to !== null
              ? `${fmtHeight(index.indexed_from)} to ${fmtHeight(index.indexed_to)}`
              : 'it has read'}
            . Nothing is left out of this table for being unused — <em>never</em> is an answer, and
            for a chain this young it is the more informative one. Three kinds read{' '}
            <em>not indexed</em>: they fire every block and name no account, so they are skipped on
            the way in rather than counted at zero.
          </p>
        </>
      )}
    </section>
  )
}
